import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function daysLeft(endDate) {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / 86400000);
}

function BatchDaysChip({ endDate }) {
  const d = daysLeft(endDate);
  if (d === null) return null;
  if (d < 0) return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">Overdue</span>;
  if (d <= 30) return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">{d}d left</span>;
  return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">{d}d left</span>;
}

function StaffAvatar({ name, imagePath, large = false }) {
  const [error, setError] = useState(false);
  const initials = (name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const base = large ? 'w-10 h-10 text-xs' : 'w-7 h-7 text-[9px]';
  if (!error && imagePath) {
    return <img src={getImageURL(imagePath)} alt={name} onError={() => setError(true)} className={`${base} rounded-full shrink-0 object-cover border-2 border-white shadow-sm ring-1 ring-gray-200`} />;
  }
  return (
    <div className={`${base} rounded-full shrink-0 border-2 border-white shadow-sm ring-1 ring-gray-200 bg-gradient-to-br from-teal-100 to-indigo-100 flex items-center justify-center`}>
      <span className="font-bold text-teal-700 leading-none">{initials}</span>
    </div>
  );
}

function SubjectStatusBadge({ status }) {
  const cfg = {
    ongoing:  'bg-emerald-100 text-emerald-700',
    completed: 'bg-blue-100 text-blue-700',
    'not started': 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${cfg[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || '—'}
    </span>
  );
}

// ─── Subject row ─────────────────────────────────────────────────────────────

function SubjectRow({ subject }) {
  const today = new Date();
  const endDate = subject.endDate ? new Date(subject.endDate) : null;
  const startDate = subject.startDate ? new Date(subject.startDate) : null;

  const isOverdue       = subject.status !== 'completed' && endDate && endDate < today;
  const isOverScheduled = subject.scheduledClassesCount > subject.expectedNumberOfClasses && subject.expectedNumberOfClasses > 0;
  const isUnderScheduled = subject.status !== 'completed' && subject.balanceClasses > 0 && subject.expectedNumberOfClasses > 0;
  const notStartedPastStart = subject.status === 'not started' && startDate && startDate < today;

  const scheduledPct = subject.expectedNumberOfClasses > 0
    ? Math.min(100, Math.round((subject.scheduledClassesCount / subject.expectedNumberOfClasses) * 100))
    : null;

  const overdueDays = isOverdue ? Math.abs(daysLeft(subject.endDate)) : 0;
  const remainingDays = !isOverdue && endDate ? daysLeft(subject.endDate) : null;

  const barColor = isOverScheduled ? 'bg-orange-500'
    : scheduledPct >= 80 ? 'bg-emerald-500'
    : scheduledPct >= 50 ? 'bg-blue-500'
    : 'bg-amber-400';

  return (
    <div className={`px-5 py-4 border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50/70 ${isOverdue ? 'border-l-2 border-l-red-400' : isOverScheduled ? 'border-l-2 border-l-orange-400' : 'border-l-2 border-l-transparent'}`}>
      <div className="flex gap-4">

        {/* Lecturer */}
        <div className="flex flex-col items-center gap-1.5 w-16 shrink-0">
          <StaffAvatar name={subject.staffName} imagePath={subject.staffImagePath} large />
          <p className="text-[10px] font-medium text-gray-600 text-center leading-tight line-clamp-2">{subject.staffName || 'Unassigned'}</p>
          <p className="text-[9px] text-gray-400 uppercase tracking-wide">Lecturer</p>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">

          {/* Subject name + badges */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-bold text-gray-900">{subject.subjectName}</span>
                {subject.subjectCode && subject.subjectCode !== subject.subjectName && (
                  <span className="text-[10px] text-gray-400 font-medium">({subject.subjectCode})</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {subject.semester && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-semibold border border-indigo-100">Sem {subject.semester}</span>
                )}
                <SubjectStatusBadge status={subject.status} />
                {isOverdue && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-bold border border-red-200">⚠ Overdue {overdueDays}d</span>
                )}
                {isOverScheduled && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold border border-orange-200">Over-scheduled</span>
                )}
                {notStartedPastStart && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold border border-amber-200">Past start date</span>
                )}
              </div>
            </div>
          </div>

          {/* Date row */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z" />
              </svg>
              <span className="text-gray-500">Start</span>
              <span className="font-semibold text-gray-700">{fmtDate(subject.startDate)}</span>
            </div>
            <span className="text-gray-300 text-xs">→</span>
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-gray-500">End</span>
              <span className="font-semibold text-gray-700">{fmtDate(subject.endDate)}</span>
            </div>
            {isOverdue && (
              <span className="text-[10px] font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full">{overdueDays}d overdue</span>
            )}
            {remainingDays !== null && !isOverdue && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${remainingDays <= 14 ? 'text-amber-600 bg-amber-50' : 'text-gray-500 bg-gray-100'}`}>
                {remainingDays}d remaining
              </span>
            )}
          </div>

          {/* Hours + progress */}
          {(() => {
            const HOURS_PER_CLASS = 2;
            const allocClasses = subject.expectedNumberOfClasses;
            const allocHours   = allocClasses * HOURS_PER_CLASS;
            const schedClasses = subject.scheduledClassesCount;
            const schedHours   = schedClasses * HOURS_PER_CLASS;
            const diffClasses  = Math.abs(schedClasses - allocClasses);
            const diffHours    = diffClasses * HOURS_PER_CLASS;
            return (
              <div className="bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100">
                <div className="flex items-center gap-2 flex-wrap mb-2">

                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mr-1">Hours</span>

                  {/* Allocated */}
                  <div className="inline-flex items-center rounded-md bg-white border border-indigo-100 shadow-sm overflow-hidden">
                    <span className="text-[9px] font-bold text-indigo-400 uppercase px-2 py-1 bg-indigo-50 border-r border-indigo-100">Allocated</span>
                    <span className="px-2 py-1 text-[11px] font-bold text-indigo-700">{allocClasses} cls</span>
                    <span className="text-indigo-300 text-[10px]">·</span>
                    <span className="px-2 py-1 text-[11px] font-bold text-indigo-700">{allocHours} hrs</span>
                  </div>

                  {/* Scheduled */}
                  <div className={`inline-flex items-center rounded-md border shadow-sm overflow-hidden ${isOverScheduled ? 'bg-orange-50 border-orange-200' : 'bg-white border-blue-100'}`}>
                    <span className={`text-[9px] font-bold uppercase px-2 py-1 border-r ${isOverScheduled ? 'text-orange-400 bg-orange-100 border-orange-200' : 'text-blue-400 bg-blue-50 border-blue-100'}`}>Scheduled</span>
                    <span className={`px-2 py-1 text-[11px] font-bold ${isOverScheduled ? 'text-orange-700' : 'text-blue-700'}`}>{schedClasses} cls</span>
                    <span className={`text-[10px] ${isOverScheduled ? 'text-orange-300' : 'text-blue-300'}`}>·</span>
                    <span className={`px-2 py-1 text-[11px] font-bold ${isOverScheduled ? 'text-orange-700' : 'text-blue-700'}`}>{schedHours} hrs</span>
                  </div>

                  {/* Remaining */}
                  {isUnderScheduled && (
                    <div className="inline-flex items-center rounded-md bg-white border border-amber-200 shadow-sm overflow-hidden">
                      <span className="text-[9px] font-bold text-amber-400 uppercase px-2 py-1 bg-amber-50 border-r border-amber-200">Remaining</span>
                      <span className="px-2 py-1 text-[11px] font-bold text-amber-700">{diffClasses} cls</span>
                      <span className="text-amber-300 text-[10px]">·</span>
                      <span className="px-2 py-1 text-[11px] font-bold text-amber-700">{diffHours} hrs</span>
                    </div>
                  )}

                  {/* Over by */}
                  {isOverScheduled && (
                    <div className="inline-flex items-center rounded-md bg-orange-50 border border-orange-300 shadow-sm overflow-hidden">
                      <span className="text-[9px] font-bold text-orange-500 uppercase px-2 py-1 bg-orange-100 border-r border-orange-300">Over by</span>
                      <span className="px-2 py-1 text-[11px] font-bold text-orange-700">{diffClasses} cls</span>
                      <span className="text-orange-300 text-[10px]">·</span>
                      <span className="px-2 py-1 text-[11px] font-bold text-orange-700">{diffHours} hrs</span>
                    </div>
                  )}

                </div>

                {scheduledPct !== null && (
                  <div className="flex items-center gap-2.5">
                    <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, scheduledPct)}%` }} />
                    </div>
                    <span className={`text-[11px] font-bold w-9 text-right ${isOverScheduled ? 'text-orange-600' : scheduledPct >= 80 ? 'text-emerald-600' : 'text-gray-600'}`}>
                      {scheduledPct}%
                    </span>
                    {subject.avgAttendance > 0 && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ml-1 ${subject.avgAttendance < 75 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                        Avg {subject.avgAttendance}% attendance
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}

// ─── Batch panel ─────────────────────────────────────────────────────────────

function BatchPanel({ batch }) {
  const [open, setOpen] = useState(false);
  const [subjects, setSubjects] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (subjects !== null) { setOpen((v) => !v); return; }
    setOpen(true);
    setLoading(true);
    axiosInstance
      .get(`/admin/batches/${batch.batchId}/subjects`)
      .then((res) => setSubjects(res.data?.subjects || []))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load subjects'))
      .finally(() => setLoading(false));
  }, [batch.batchId, subjects]);

  const ongoingSubjects = (subjects?.filter((s) => s.status === 'ongoing') || [])
    .sort((a, b) => {
      const pctA = a.expectedNumberOfClasses > 0 ? a.scheduledClassesCount / a.expectedNumberOfClasses : 0;
      const pctB = b.expectedNumberOfClasses > 0 ? b.scheduledClassesCount / b.expectedNumberOfClasses : 0;
      return pctB - pctA;
    });

  return (
    <div className="border-b border-gray-100 last:border-0">
      {/* Batch header row — click to expand */}
      <button
        type="button"
        onClick={load}
        className="w-full text-left px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 hover:bg-gray-50 transition-colors"
      >
        {/* Expand chevron */}
        <svg xmlns="http://www.w3.org/2000/svg"
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>

        {/* Batch name */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{batch.batchInternalName || batch.batchName}</span>
            {batch.batchInternalName && batch.batchName !== batch.batchInternalName && (
              <span className="text-[10px] text-gray-400">({batch.batchName})</span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">Ongoing</span>
            {batch.batchSubjectsCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">
                {batch.batchSubjectsCount} subject{batch.batchSubjectsCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {batch.whatsappGroupName && (
            <p className="text-[11px] text-gray-400 mt-0.5">WhatsApp: {batch.whatsappGroupName}</p>
          )}
        </div>

        {/* Dates */}
        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
          <span className="text-[11px] text-gray-500 tabular-nums">{fmtDate(batch.batchStartingDate)}</span>
          <span className="text-gray-300">→</span>
          <span className="text-[11px] text-gray-500 tabular-nums">{fmtDate(batch.batchEndingDate)}</span>
          <BatchDaysChip endDate={batch.batchEndingDate} />
        </div>
      </button>

      {/* Expanded subjects */}
      {open && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full w-6 h-6 border-2 border-gray-200 border-t-teal-600" />
            </div>
          )}
          {error && (
            <p className="px-5 py-3 text-xs text-red-500">{error}</p>
          )}
          {!loading && !error && subjects !== null && subjects.length === 0 && (
            <p className="px-5 py-3 text-xs text-gray-400">No subjects assigned to this batch.</p>
          )}
          {!loading && !error && subjects !== null && (
            ongoingSubjects.length === 0 ? (
              <p className="px-5 py-3 text-xs text-gray-400">No ongoing subjects for this batch.</p>
            ) : (
              <div>
                <div className="px-5 py-1.5 bg-emerald-50 border-b border-emerald-100">
                  <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Ongoing Subjects ({ongoingSubjects.length})</span>
                </div>
                {ongoingSubjects.map((s) => <SubjectRow key={s.id} subject={s} />)}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CourseWiseBatchesReport() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [scheduleFilter, setScheduleFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axiosInstance
      .get('/admin/batches', { params: { status: 'ongoing' } })
      .then((res) => { if (!cancelled) setBatches(Array.isArray(res.data?.batches) ? res.data.batches : []); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || 'Failed to load batches'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const courseGroups = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = batches.filter((b) => {
      const matchSearch = !q
        || (b.batchName || '').toLowerCase().includes(q)
        || (b.batchInternalName || '').toLowerCase().includes(q)
        || (b.course?.fullName || '').toLowerCase().includes(q)
        || (b.course?.shortName || '').toLowerCase().includes(q);
      const matchSchedule = scheduleFilter === 'all' || (b.course?.scheduleType || '').toLowerCase() === scheduleFilter;
      return matchSearch && matchSchedule;
    });
    const map = {};
    filtered.forEach((b) => {
      const key = b.course?.fullName || 'Unknown Course';
      if (!map[key]) map[key] = { courseName: key, shortName: b.course?.shortName || '', scheduleType: b.course?.scheduleType || null, batches: [] };
      map[key].batches.push(b);
    });
    return Object.values(map)
      .sort((a, b) => a.courseName.localeCompare(b.courseName))
      .map((g) => ({ ...g, batches: g.batches.sort((a, b) => new Date(a.batchStartingDate || 0) - new Date(b.batchStartingDate || 0)) }));
  }, [batches, search, scheduleFilter]);

  const totalBatches = courseGroups.reduce((s, g) => s + g.batches.length, 0);

  return (
    <DashboardLayout title="Course-wise Ongoing Batches">
      <div className="min-h-screen bg-gray-50 px-6 py-8">

        {/* Header */}
        <div className="mb-6 border-b border-gray-200 pb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-gray-900">Course-wise Ongoing Batches</h1>
            </div>
            <p className="text-sm text-gray-500 ml-10">All currently active batches grouped by course — click a batch to expand subjects</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-gray-50 transition-colors shrink-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        {/* Summary */}
        {!loading && !error && (
          <div className="flex flex-wrap gap-3 mb-6">
            {[
              { label: 'Courses', value: courseGroups.length, color: 'text-teal-700' },
              { label: 'Ongoing Batches', value: totalBatches, color: 'text-teal-700' },
              { label: 'Weekday', value: batches.filter((b) => (b.course?.scheduleType || '').toLowerCase() === 'weekday').length, color: 'text-blue-700' },
              { label: 'Weekend', value: batches.filter((b) => (b.course?.scheduleType || '').toLowerCase() === 'weekend').length, color: 'text-purple-700' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-4 py-2.5 shadow-sm">
                <span className="text-xs text-gray-500">{label}</span>
                <span className={`text-sm font-bold ${color}`}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by batch name or course…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <select
            value={scheduleFilter}
            onChange={(e) => setScheduleFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Schedule Types</option>
            <option value="weekday">Weekday</option>
            <option value="weekend">Weekend</option>
          </select>
        </div>

        {/* States */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full w-10 h-10 border-4 border-gray-200 border-t-teal-600" />
          </div>
        )}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">{error}</div>
        )}
        {!loading && !error && courseGroups.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <p className="text-sm">No ongoing batches found.</p>
          </div>
        )}

        {/* Course groups */}
        {!loading && !error && courseGroups.length > 0 && (
          <div className="space-y-6">
            {courseGroups.map((group) => (
              <div key={group.courseName} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

                {/* Course header */}
                <div className="px-5 py-3 bg-teal-50 border-b border-teal-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 bg-teal-600 rounded-lg flex items-center justify-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm font-bold text-teal-900 truncate">{group.courseName}</h2>
                      {group.shortName && <p className="text-[10px] text-teal-600">{group.shortName}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {group.scheduleType && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${group.scheduleType.toLowerCase() === 'weekday' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {group.scheduleType.charAt(0).toUpperCase() + group.scheduleType.slice(1)}
                      </span>
                    )}
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                      {group.batches.length} ongoing batch{group.batches.length !== 1 ? 'es' : ''}
                    </span>
                  </div>
                </div>

                {/* Batches (expandable) */}
                <div>
                  {group.batches.map((batch) => <BatchPanel key={batch.batchId} batch={batch} />)}
                </div>

              </div>
            ))}
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
